import Feature from 'ol/Feature';
import {
  Point, LineString, Polygon, Circle, MultiPolygon, MultiLineString, MultiPoint,
} from 'ol/geom';
import { fromCircle } from 'ol/geom/Polygon';
import {
  fromLonLat, toLonLat, circleRadiusFromWMS, circleRadiusToWMS,
} from '../projection'

const CIRCLE_USER_GEOMETRY = 'Circle'
const AREA_USER_GEOMETRY = 'Area'
const LINE_USER_GEOMETRY = 'Line'
const POINT_USER_GEOMETRY = 'Point'

// Le nombre de points du polygone qui est la traduction de la geometrie cercle.
const NUMBER_POINT_CIRCLE_TO_POLYGON = 180;
// The object build instruction attribute name.
export const GEOMETRY_INSTRUCTION_ATTRIBUTE_NAME = 'Geometry Instruction';

/**
 * Genre les transformation entre Feature et GeoJSONFeature. Notamment les modifications de géometries entre édition et geométries sur le serveur.
 */
export default class EditableFeature extends Feature {
  /**
     *
     * @param {Feature} feature
     * @return {EditableFeature}
     */
  static toEditableFeature(feature) {
    const editableFeature = new EditableFeature();
    /** @type {string} */
    const objectGeometryOnEdit = feature.getProperties()[GEOMETRY_INSTRUCTION_ATTRIBUTE_NAME];
    let editableGeometry;
    if (objectGeometryOnEdit) {
      const loadedObjectBuildCoords = loadObjectBuildInstructionCoords(objectGeometryOnEdit.substring(1));
      switch (loadedObjectBuildCoords && objectGeometryOnEdit[0]) {
        case 'p': // Point `p[cx,cy]`
          editableGeometry = new Point(loadedObjectBuildCoords[0]);
          break;
        case 'l': // LineString `l[c1x,c1y,c2x,c2y]`
          editableGeometry = new LineString(loadedObjectBuildCoords);
          break;
        case 'a': // Polygon `a[c1x,c1y,...,cnx,cny]`
          editableGeometry = new Polygon(loadedObjectBuildCoords);
          break;
        case 'c': { // Circle `c[c0x,c0y,radius]`
          const geometry = JSON.parse(objectGeometryOnEdit.substring(1));
          const radius = circleRadiusFromWMS(geometry[1], geometry[2]);
          editableGeometry = new Circle(loadedObjectBuildCoords[0], radius);
          break;
        }
        default:
          editableGeometry = feature.getGeometry();
          break;
      }
    } else {
      editableGeometry = feature.getGeometry();
    }
    editableFeature.setProperties(feature.getProperties());
    editableFeature.setGeometry(editableGeometry);
    return editableFeature;
  }

  /**
     * @constructor
     */
  constructor() {
    super();

    this.geometryExport_ = DEFAULT_GEOMETRY_EXPORT;
  }

  /**
     * Set the export function.
     * @param {FeatureExportFunction} exportFunction
     */
  setExportFunction(exportFunction) {
    this.geometryExport_ = exportFunction;
  }

  /**
     * Export feature to send to the map server.
     * @return {Feature}
     */
  export() {
    const feature = this.clone();
    const geom = this.getGeometry();
    feature.set(GEOMETRY_INSTRUCTION_ATTRIBUTE_NAME, this.geometryExport_.editInstruction(geom));
    feature.setGeometry(this.geometryExport_.geometry(geom));
    return feature;
  }

  /** @type {Object<string,*>} */
  get properties() {
    return this.getProperties();
  }

  set properties(properties) {
    this.setProperties(properties);
  }

  /**
     * Turn the feature {@link USER_GEOMETRY} (Point, Line, Area, Circle).
     * @return {USER_GEOMETRY} The edition geometry.
     */
  getUserGeometry() {
    const geom = this.getGeometry();
    let userGeometry;
    if (geom instanceof Circle) {
      userGeometry = CIRCLE_USER_GEOMETRY;
    } else if (geom instanceof Polygon || geom instanceof MultiPolygon) {
      userGeometry = AREA_USER_GEOMETRY;
    } else if (geom instanceof LineString || geom instanceof MultiLineString) {
      userGeometry = LINE_USER_GEOMETRY;
    } else if (geom instanceof Point || geom instanceof MultiPoint) {
      userGeometry = POINT_USER_GEOMETRY;
    }
    return userGeometry;
  }
}

/**
 * @typedef {Object} FeatureExportFunction
 * Function that modify the feature geometry in {@link EditFeatureLayer} to send to the map server.
 * @property {function(import("ol/geom/Geometry").default):import("ol/geom/Geometry").default} geometry Parse geometry
 * @property {function(import("ol/geom/Geometry").default):string} editInstruction set the "geometry instruction" attribute if it's a circle.
 */

/**
 * The default geometry transformer.
 * @type {FeatureExportFunction}
 */
export const DEFAULT_GEOMETRY_EXPORT = {
  geometry: (geom) => {
    if (geom instanceof Circle) {
      geom = fromCircle(geom, NUMBER_POINT_CIRCLE_TO_POLYGON);
    }
    return geom;
  },
  editInstruction: (geom) => {
    if (geom instanceof Circle) {
      const coords = toLonLat(geom.getCenter());
      const radius = circleRadiusToWMS(coords[1], geom.getRadius());
      return `c[${coords},${radius}]`;
    }
  },
};

/**
 * Load the Object build instruction to coords array.
 * @param {string} coords The flat list of number.
 * @return {Array<import('ol/coordinate').Coordinate>} The coords arrray.
 */
function loadObjectBuildInstructionCoords(coords) {
  try {
    const coordsArray = JSON.parse(coords);
    const ret = [];
    for (let i = 0; i + 1 < coordsArray.length; i += 2) {
      ret.push(fromLonLat([coordsArray[i], coordsArray[i + 1]]));
    }
    return ret;
  } catch (e) {
    return null;
  }
}
